import { TABLE_VIEW_ROW_HEIGHT } from "@shared/constants/index";

import type {
  ListRouteMatchOptions,
  ScrollModule,
  ScrollRestorationRoute,
} from "~/constants/scrollRestorationRoutes";

export const SCROLL_PERSIST_SUFFIX = "VIEW_PERSIST";

export const SCROLL_KIND_VIRTUAL_TABLE = "virtualTable";

export interface ScrollPersistState {
  x?: number;
  y?: number;
  index?: number;
  scrollTop?: number;
  scrollRowIndex?: number;
  anchorRowId?: string;
  isVirtualList?: boolean;
  container?: "main" | "element";
  scrollKind?: string;
  listQueryKey?: string;
  listItemsLoaded?: number;
}

export type ListScrollPrefetchPatch = Partial<ScrollPersistState>;

export const getScrollStorageKey = (scope: string): string =>
  `${scope}-${SCROLL_PERSIST_SUFFIX}`;

export const getMainScrollElement = (): HTMLElement | null =>
  document.querySelector("main.overflow-y-auto");

export const clampScrollTop = (
  element: HTMLElement | null,
  scrollTop: number,
): number => {
  if (!element) return 0;
  const maxScroll = Math.max(0, element.scrollHeight - element.clientHeight);
  return Math.min(Math.max(0, scrollTop), maxScroll);
};

export const readScrollState = (scope: string): ScrollPersistState | null => {
  try {
    const raw = sessionStorage.getItem(getScrollStorageKey(scope));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as unknown;
    // Guard against legacy/foreign values (strings, numbers) written under a
    // scroll scope key - callers all expect a ScrollPersistState object.
    if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) {
      return null;
    }
    return parsed as ScrollPersistState;
  } catch {
    return null;
  }
};

export const writeScrollState = (
  scope: string,
  state: ScrollPersistState,
): void => {
  sessionStorage.setItem(getScrollStorageKey(scope), JSON.stringify(state));
};

export const clearScrollState = (scope: string): void => {
  sessionStorage.removeItem(getScrollStorageKey(scope));
};

const pathnameMatchesList = (
  pathname: string,
  route: ScrollRestorationRoute,
): boolean => {
  const paths = route.listPaths?.length ? route.listPaths : [route.listPath];
  return paths.some((p) => pathname === p || pathname.startsWith(`${p}?`));
};

export const matchListRoute = (
  pathname: string,
  routes: ScrollRestorationRoute[],
  options: ListRouteMatchOptions = {},
): ScrollRestorationRoute | null => {
  const { resolveAmbiguousListRoute } = options;
  const matches = routes.filter((route) =>
    pathnameMatchesList(pathname, route),
  );
  if (!matches.length) return null;
  if (matches.length === 1) return matches[0];
  if (typeof resolveAmbiguousListRoute === "function") {
    return resolveAmbiguousListRoute(pathname, matches) ?? matches[0];
  }
  return matches[0];
};

export const matchDetailRoute = (
  pathname: string,
  routes: ScrollRestorationRoute[],
): ScrollRestorationRoute | null => {
  const ordered = [...routes].sort((a, b) => {
    const aInstance = a.detailPattern.source.includes("instance") ? -1 : 0;
    const bInstance = b.detailPattern.source.includes("instance") ? -1 : 0;
    return aInstance - bInstance;
  });
  for (const route of ordered) {
    if (route.detailPattern.test(pathname)) {
      return route;
    }
  }
  return null;
};

export const resolveScopeKey = (
  route: ScrollRestorationRoute,
  pathname: string,
): string => {
  if (typeof route.resolveScope === "function") {
    return route.resolveScope(pathname);
  }
  return route.scope || route.listPath;
};

export const getScrollModuleForPathname = (
  pathname: string,
  routes: ScrollRestorationRoute[],
  listMatchOptions: ListRouteMatchOptions = {},
): ScrollModule | null => {
  const listRoute = matchListRoute(pathname, routes, listMatchOptions);
  if (listRoute?.module) return listRoute.module;
  const detailRoute = matchDetailRoute(pathname, routes);
  if (detailRoute?.module) return detailRoute.module;
  return null;
};

export const clearModuleScrollStates = (
  moduleId: ScrollModule,
  scopesByModule: Record<ScrollModule, string[]>,
): void => {
  const scopes = scopesByModule[moduleId];
  if (!scopes) return;
  scopes.forEach((scope) => clearScrollState(scope));
};

export const resetMainScrollElement = (): void => {
  const main = getMainScrollElement();
  if (main) {
    main.scrollTo({ top: 0, left: 0 });
  }
};

export const resetModuleViewsScroll = (
  moduleId: ScrollModule,
  scopesByModule: Record<ScrollModule, string[]>,
): void => {
  clearModuleScrollStates(moduleId, scopesByModule);
  resetMainScrollElement();
};

export const resolveListScopeForPathname = (
  pathname: string,
  routes: ScrollRestorationRoute[],
): string | null => {
  const listRoute = matchListRoute(pathname, routes);
  if (listRoute) {
    return resolveScopeKey(listRoute, pathname);
  }
  const detailRoute = matchDetailRoute(pathname, routes);
  if (detailRoute) {
    return resolveScopeKey(detailRoute, detailRoute.listPath);
  }
  return null;
};

export const persistScrollForScope = (
  scope: string,
  getScrollContainer?: (scope: string) => HTMLElement | null,
  listPrefetch?: ListScrollPrefetchPatch,
): void => {
  if (!scope) return;
  const existing = readScrollState(scope) || {};
  const registered = getScrollContainer?.(scope);
  const main = getMainScrollElement();
  const prefetch =
    listPrefetch && typeof listPrefetch === "object" ? listPrefetch : {};
  const explicitY =
    typeof prefetch.y === "number" && !Number.isNaN(prefetch.y)
      ? prefetch.y
      : null;

  if (explicitY !== null) {
    const isVirtualTable = prefetch.scrollKind === SCROLL_KIND_VIRTUAL_TABLE;
    const scrollRowIndex =
      typeof prefetch.scrollRowIndex === "number"
        ? prefetch.scrollRowIndex
        : Math.max(0, Math.floor(explicitY / TABLE_VIEW_ROW_HEIGHT));
    writeScrollState(scope, {
      ...existing,
      ...prefetch,
      x: existing.x ?? 0,
      y: explicitY,
      scrollRowIndex: isVirtualTable ? scrollRowIndex : existing.scrollRowIndex,
      anchorRowId:
        isVirtualTable && prefetch.anchorRowId
          ? prefetch.anchorRowId
          : existing.anchorRowId,
      index: 0,
      isVirtualList: false,
      container: "element",
    });
    return;
  }

  if (registered && typeof registered.scrollTop === "number") {
    const y = registered.scrollTop;
    const isVirtualTable = prefetch.scrollKind === SCROLL_KIND_VIRTUAL_TABLE;
    const scrollRowIndex =
      typeof prefetch.scrollRowIndex === "number"
        ? prefetch.scrollRowIndex
        : Math.max(0, Math.floor(y / TABLE_VIEW_ROW_HEIGHT));
    writeScrollState(scope, {
      ...existing,
      ...prefetch,
      x: registered.scrollLeft,
      y,
      scrollRowIndex: isVirtualTable ? scrollRowIndex : existing.scrollRowIndex,
      anchorRowId:
        isVirtualTable && prefetch.anchorRowId
          ? prefetch.anchorRowId
          : existing.anchorRowId,
      index: 0,
      isVirtualList: false,
      container: "element",
    });
    return;
  }

  if (main && prefetch.scrollKind !== SCROLL_KIND_VIRTUAL_TABLE) {
    writeScrollState(scope, {
      ...existing,
      ...prefetch,
      x: main.scrollLeft,
      y: main.scrollTop,
      index: 0,
      isVirtualList: false,
      container: "main",
    });
  }
};

const RESTORE_RETRY_MS = [0, 50, 150, 300, 600, 1000];

export interface RestoreScrollOptions {
  // When it returns true, any pending retry stops re-applying the saved
  // offset. Lets the caller abort the retry cascade once the user starts
  // scrolling manually, so restoration never fights live user input.
  isCancelled?: () => boolean;
}

export const restoreScrollForScope = (
  scope: string,
  getScrollContainer?: (scope: string) => HTMLElement | null,
  options: RestoreScrollOptions = {},
): ScrollPersistState | null | undefined => {
  const { isCancelled } = options;
  const saved = readScrollState(scope);
  if (!saved) return;

  if (saved.isVirtualList) {
    return saved;
  }

  const run = (): boolean => {
    if (isCancelled?.()) return false;

    const registered = getScrollContainer?.(scope);
    const target =
      registered ||
      (saved.container === "main" ? getMainScrollElement() : null);

    if (!target) return false;

    const top = clampScrollTop(target, saved.y ?? 0);
    target.scrollTo({
      top,
      left: Math.max(0, saved.x ?? 0),
    });
    return true;
  };

  RESTORE_RETRY_MS.forEach((delay) => {
    setTimeout(() => {
      run();
    }, delay);
  });

  requestAnimationFrame(() => {
    run();
  });

  return saved;
};
